import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { ForbiddenError } from "@/api/errors";
import { ROUTES } from "@/app/routes";
import { BackLink } from "@/components/layout/back-link";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CASE_CONFIG,
  CASE_DATA_FIELDS,
  DECISION_COMMENT_METADATA_KEY,
  DECISION_METADATA_KEY,
  TITLE_METADATA_KEY,
} from "@/domain/case/case-config";
import { ProcessOverview } from "@/features/case-diagram/process-overview";
import { ReturnedReason } from "@/features/my-applications/returned-reason";
import { ProcessDescription } from "@/features/process-design/process-description";
import { formatCaseNumber } from "@/lib/format/case-number";

import { CaseActionSection } from "./case-action-section";
import { deriveDecidedBy } from "./derive-decided-by";
import { formatCurrency, formatDateTime } from "@/lib/format/datetime";

import {
  type CaseHistoryEvent,
  type CaseRef,
  useCaseDetail,
  useCaseHistory,
} from "./use-case-detail";

/**
 * 案件詳細のページ。
 *
 * 以前はドロワー（サイドパネル）だったが、概要・フロー図・履歴を読むには幅が
 * 足りなかった。ページにして画面を大きく使う。URL に案件番号とモデル id を含める
 * ので、リンクの共有や再読込でも同じ画面に戻れる。
 */
export function CaseDetailPage() {
  const { t } = useTranslation(["common", "domain"]);
  const params = useParams();

  const caseId = Number(params.caseId);
  const processModelId = (params.modifiedModelId ?? "").replace(/:/g, "/");
  const caseRef: CaseRef | null =
    Number.isInteger(caseId) && caseId > 0 && processModelId !== ""
      ? { caseId, processModelId }
      : null;

  return (
    <section className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <BackLink
          fallback={{
            to: ROUTES.myApplications,
            label: t("common:caseDetail.back"),
          }}
        />
        <h1 className="text-xl font-semibold">
          {caseRef === null
            ? t("common:caseDetail.title")
            : `${t("common:caseDetail.title")} ${formatCaseNumber(caseRef.caseId)}`}
        </h1>
      </header>

      {caseRef === null ? (
        <p role="alert" className="text-destructive text-sm">
          {t("common:caseDetail.notFound")}
        </p>
      ) : (
        <>
          {/* 分量が少ない（概要 9 行・履歴 4〜9 件）のでタブで分けず縦に並べる。
              節に id を振ってアンカーで直接飛べるようにする */}
          <CaseActionSection caseRef={caseRef} />
          <SummarySection caseRef={caseRef} />
          <ProcessOverview caseRef={caseRef} />
          <ProcessDescription processModelId={caseRef.processModelId} />
          <HistorySection caseRef={caseRef} />
        </>
      )}
    </section>
  );
}

function SummarySection({ caseRef }: { readonly caseRef: CaseRef }) {
  const { t } = useTranslation(["common", "domain"]);
  const { data, isPending, isError, error } = useCaseDetail(caseRef);
  // 履歴セクションと同じクエリなので追加の取得は発生しない
  const history = useCaseHistory(caseRef);
  const decidedBy = deriveDecidedBy(history.data ?? [], CASE_CONFIG);

  if (isPending) return <Skeleton className="h-40 w-full" />;
  if (error instanceof ForbiddenError) {
    // 導線（全案件モニタ・操作履歴）は権限で閉じているが、リンク共有や
    // ブックマークからは来られる。「壊れている」と区別できるよう理由を出す
    return (
      <p role="alert" className="text-destructive text-sm">
        {t("common:caseDetail.forbidden")}
      </p>
    );
  }
  if (isError || !data) {
    return (
      <p role="alert" className="text-destructive text-sm">
        {t("common:state.error")}
      </p>
    );
  }

  // 項目はモデルごとに違うので metadata をそのまま並べ、既知のキーだけ
  // ラベルと書式を当てる（固定の項目表に頼ると他のモデルで何も出ない）。
  // 件名の予約キーは入力項目ではなく一覧のための複製なので除く（見出しに出ている）
  const entries = Object.entries(data.metadata).filter(
    ([key, value]) => value !== "" && key !== TITLE_METADATA_KEY,
  );

  // 判定者を差し込む位置。判定コメントの直後に置き、コメントが無い案件では
  // 判定の直後にする（どちらも無ければ最後に置く）
  const keys = entries.map(([key]) => key);
  const decidedByAnchor =
    [DECISION_COMMENT_METADATA_KEY, DECISION_METADATA_KEY].find((key) =>
      keys.includes(key),
    ) ?? keys.at(-1);

  return (
    <section id="summary" className="flex flex-col gap-4 text-sm">
      <h2 className="text-sm font-semibold">
        {t("common:caseDetail.sections.summary")}
      </h2>

      {/* 差し戻しは真っ先に伝える（他の項目に埋もれさせない） */}
      <ReturnedReason metadata={data.metadata} />

      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
        <Row label={t("domain:applicationType")}>
          {data.processModelDisplayName}
        </Row>
        <Row label={t("common:caseDetail.initiator")}>
          {data.initiator ?? "—"}
        </Row>
        <Row label={t("common:caseDetail.startedAt")}>
          {formatDateTime(data.startedAt)}
        </Row>
        <Row label={t("common:caseDetail.endedAt")}>
          {formatDateTime(data.endedAt)}
        </Row>
      </dl>

      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold">
          {t("common:caseDetail.caseContent")}
        </h3>
        {entries.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {t("common:state.empty")}
          </p>
        ) : (
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
            {entries.map(([key, value]) => {
              const known = CASE_DATA_FIELDS.find((field) => field.key === key);

              return (
                <Fragment key={key}>
                  <Row label={known?.label ?? key}>
                    {known?.format === "currency"
                      ? formatCurrency(value)
                      : value}
                  </Row>
                  {/*
                    判定した人。metadata には無いので履歴から導く（履歴セクションと
                    同じクエリなので追加の取得は無い）。判定に紐づく情報なので
                    判定コメントの直後に置く。
                  */}
                  {key === decidedByAnchor && decidedBy !== null ? (
                    <Row label={t("common:caseDetail.decidedBy")}>
                      {decidedBy}
                    </Row>
                  ) : null}
                </Fragment>
              );
            })}
          </dl>
        )}
      </section>
    </section>
  );
}

function HistorySection({ caseRef }: { readonly caseRef: CaseRef }) {
  const { t } = useTranslation("common");
  const { data, isPending, isError } = useCaseHistory(caseRef);

  if (isPending) return <Skeleton className="h-40 w-full" />;
  if (isError || !data) {
    return (
      <p role="alert" className="text-destructive text-sm">
        {t("state.error")}
      </p>
    );
  }
  if (data.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        {t("caseDetail.noHistory")}
      </p>
    );
  }

  return (
    <section id="history" className="flex flex-col gap-2">
      <h2 className="text-sm font-semibold">
        {t("caseDetail.sections.history")}
      </h2>
      <ol className="flex flex-col gap-2 text-sm">
        {data.map((event) => (
          <HistoryRow key={event.id} event={event} />
        ))}
      </ol>
    </section>
  );
}

function HistoryRow({ event }: { readonly event: CaseHistoryEvent }) {
  const { t } = useTranslation("common");

  return (
    <li className="border-border flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b pb-2 last:border-0">
      <span className="text-muted-foreground w-36 shrink-0 tabular-nums">
        {formatDateTime(event.occurredAt)}
      </span>
      <Badge variant="outline">
        {/* 未知の event_type は生の値をそのまま出す（監査で欠落させない） */}
        {t(`caseDetail.eventTypes.${event.eventType}`, {
          defaultValue: event.eventType,
        })}
      </Badge>
      <span className="flex-1 font-medium">{event.taskName ?? "—"}</span>
      <span className="text-muted-foreground">{event.actor ?? "system"}</span>
    </li>
  );
}

function Row({
  label,
  children,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
}) {
  return (
    <>
      <dt className="text-muted-foreground whitespace-nowrap">{label}</dt>
      <dd className="break-words">{children}</dd>
    </>
  );
}
